import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Input,
  Select,
  Space,
  Table,
  Tabs,
  message,
} from 'antd';
import { ReloadOutlined, SortAscendingOutlined, SortDescendingOutlined } from '@ant-design/icons';
import DashboardPageWrapper from '../../components/layout/DashboardPageWrapper';
import { useAuth } from '../../contexts/AuthContext';
import { PERMISSIONS } from '../../config/permissions';
import productService from '../../services/productService';
import NewsEditDrawer from './NewsEditDrawer';
import newsService from './newsService';
import {
  buildShareFilterOptions,
  getNewsListColumns,
  SORT_OPTIONS,
  STATUS_TABS,
} from './news.helpers';

export default function NewsListPage() {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canCreate = isSuperAdmin || hasPermission(PERMISSIONS.NEWS_CREATE);
  const canEdit = isSuperAdmin || hasPermission(PERMISSIONS.NEWS_EDIT);
  const canPublish = isSuperAdmin || hasPermission(PERMISSIONS.NEWS_PUBLISH);
  const canReject = isSuperAdmin || hasPermission(PERMISSIONS.NEWS_DELETE);

  const [statusFilter, setStatusFilter] = useState('draft');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [shareFilter, setShareFilter] = useState('');
  const [sortBy, setSortBy] = useState('news_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    productService
      .getProducts({ limit: 500 })
      .then((res) => {
        const raw = res?.data?.products;
        setProducts(Array.isArray(raw) ? raw : []);
      })
      .catch(() => setProducts([]));
  }, []);

  const loadNews = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const data = await newsService.listNews({
        status: statusFilter || undefined,
        search: search || undefined,
        share_short_name: shareFilter || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        limit: pagination.limit,
      });
      setItems(data.items || []);
      setPagination(data.pagination || { page: 1, limit: 20, total: 0 });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load news.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, shareFilter, sortBy, sortOrder, pagination.limit]);

  useEffect(() => {
    loadNews(1);
  }, [loadNews]);

  const handleFetchNews = async () => {
    setFetching(true);
    try {
      const result = await newsService.runIngestion();
      message.success(
        `Fetched: ${result.accepted} new, ${result.skipped_duplicate} duplicates skipped.`,
      );
      await loadNews(1);
    } catch (err) {
      message.error(
        err?.response?.data?.message || err?.message || 'News fetch failed.',
      );
    } finally {
      setFetching(false);
    }
  };

  const openArticle = (row) => {
    setSelected(row);
    setDrawerOpen(true);
  };

  const handleSaved = async () => {
    setDrawerOpen(false);
    await loadNews(pagination.page);
  };

  const runRowAction = async (row, fn) => {
    setActionLoadingId(row.id);
    try {
      await fn();
      message.success('Updated.');
      await loadNews(pagination.page);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || 'Action failed.');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePublish = (row) =>
    runRowAction(row, () => newsService.publishNews(row.id));

  const handleUnpublish = (row) =>
    runRowAction(row, () => newsService.unpublishNews(row.id));

  const shareFilterOptions = buildShareFilterOptions(products);

  const columns = getNewsListColumns({
    onOpen: openArticle,
    onPublish: handlePublish,
    onUnpublish: handleUnpublish,
    canPublish,
    actionLoadingId,
  });

  return (
    <DashboardPageWrapper>
      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">News</h1>
            <p className="text-sm text-gray-500 mt-1">
              Review ingested private-market news and publish to investors.
            </p>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => loadNews(pagination.page)}>
              Refresh
            </Button>
            {canCreate ? (
              <Button type="primary" loading={fetching} onClick={handleFetchNews}>
                Fetch news
              </Button>
            ) : null}
          </Space>
        </div>

        {error ? <Alert type="error" message={error} showIcon /> : null}

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:flex-wrap">
          <Input.Search
            placeholder="Search headline, share, source…"
            allowClear
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full lg:max-w-xs"
          />
          <Select
            value={shareFilter}
            onChange={setShareFilter}
            options={shareFilterOptions}
            className="w-full lg:w-40"
            placeholder="Share"
          />
          <Select
            value={sortBy}
            onChange={setSortBy}
            options={SORT_OPTIONS}
            className="w-full lg:w-44"
          />
          <Button
            icon={sortOrder === 'asc' ? <SortAscendingOutlined /> : <SortDescendingOutlined />}
            onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
          >
            {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          </Button>
        </div>

        <Tabs
          activeKey={statusFilter}
          onChange={setStatusFilter}
          items={STATUS_TABS.map((tab) => ({
            key: tab.key,
            label: tab.label,
          }))}
        />

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          scroll={{ x: 1100 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            onChange: (page) => loadNews(page),
          }}
        />
      </div>

      <NewsEditDrawer
        open={drawerOpen}
        article={selected}
        products={products}
        onClose={() => setDrawerOpen(false)}
        onSaved={handleSaved}
        permissions={{ canEdit, canPublish, canReject }}
      />
    </DashboardPageWrapper>
  );
}
